import { useResetPasswordMutation } from "@graphql/auth/auth.g";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { Button } from "@design-system/Button/Button";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";
import { Text } from "@design-system/Text/Text";
import { TextField } from "@design-system/TextField/TextField";

import { routes } from "@pages/routes";

import { setTenantSchemaTag } from "../../../sentry";

type Inputs = {
  password: string;
  confirmPassword: string;
};

type Props = {
  token: string;
};

export const ResetPasswordForm = ({ token }: Props) => {
  const navigate = useNavigate();
  const resetPasswordMutation = useResetPasswordMutation();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Inputs>();

  const onSubmit: SubmitHandler<Inputs> = async (values) => {
    setFormError(null);

    try {
      const data = await resetPasswordMutation.mutateAsync({
        token,
        password: values.password,
      });

      if (data.resetPassword?.organizationSlug) {
        setTenantSchemaTag(data.resetPassword.organizationSlug);
        navigate(routes.portal.dashboard.$path(), {
          viewTransition: true,
          replace: true,
        });
      }
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : "Unable to set this password. Request a new link and try again.",
      );
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FieldGroup label="Password" error={errors.password?.message} required>
        <TextField
          type="password"
          autoComplete="new-password"
          isPasswordManagerEnabled
          {...register("password", {
            required: { value: true, message: "This field is required" },
            minLength: {
              value: 8,
              message: "Use at least 8 characters",
            },
          })}
          transparent
        />
      </FieldGroup>
      <FieldGroup
        label="Confirm password"
        error={errors.confirmPassword?.message}
        required
      >
        <TextField
          type="password"
          autoComplete="new-password"
          isPasswordManagerEnabled
          {...register("confirmPassword", {
            required: { value: true, message: "This field is required" },
            validate: (value, formValues) =>
              value === formValues.password || "Passwords do not match",
          })}
          transparent
        />
      </FieldGroup>
      {formError && (
        <Text size={12} color="danger">
          {formError}
        </Text>
      )}
      <Button isLoading={isSubmitting} stretch type="submit">
        Set password
      </Button>
    </Form>
  );
};
